/**
 * Final-payload chunking.
 *
 * Browsers cap the total body size of in-flight keepalive requests
 * (~64KB in Chrome, shared across keepalive fetches AND sendBeacon).
 * A session's final payload can easily exceed that once it contains the
 * rrweb full snapshot, so the final send is split into chunks that each
 * fit safely under the budget.
 *
 * This module is pure (no browser globals) so it can be unit-tested in node.
 */

import type { RRWebEvent } from './types';

/**
 * Greedily split events into chunks whose approximate serialized size stays
 * under maxBytes. Order is preserved. An event larger than maxBytes gets its
 * own chunk (the send will fall back to the retry queue, where size limits
 * don't apply).
 *
 * Note: JSON.stringify length counts UTF-16 code units, not UTF-8 bytes —
 * non-ASCII content is larger on the wire. Callers should keep maxBytes
 * comfortably below the real limit to absorb the skew.
 */
export function chunkEvents(events: RRWebEvent[], maxBytes: number): RRWebEvent[][] {
  const chunks: RRWebEvent[][] = [];
  let current: RRWebEvent[] = [];
  let currentSize = 2; // "[]"

  for (const event of events) {
    let eventSize: number;
    try {
      eventSize = JSON.stringify(event).length + 1; // + comma
    } catch {
      eventSize = 1024; // unserializable estimate; still include the event
    }

    if (current.length > 0 && currentSize + eventSize > maxBytes) {
      chunks.push(current);
      current = [];
      currentSize = 2;
    }
    current.push(event);
    currentSize += eventSize;
  }

  if (current.length > 0) {
    chunks.push(current);
  }
  return chunks;
}
